import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, ArrowRight, ArrowLeft, MailCheck } from "lucide-react";
import { api, formatErr } from "../lib/api";
import { isValidEmail } from "../lib/passwordStrength";
import Logo from "../components/Logo";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!isValidEmail(trimmed)) {
      setEmailError("Please enter a valid email address.");
      return;
    }
    setEmailError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: trimmed });
      setSent(true);
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-12">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-md"
        data-testid="forgot-password-page"
      >
        <div className="flex justify-center mb-7"><Logo size="lg" /></div>

        <div className="premium-card p-8 sm:p-10">
          {!sent ? (
            <>
              <h1 className="font-serif-display text-3xl text-brand-text">Forgot password?</h1>
              <p className="text-brand-muted mt-1.5">
                No worries — we'll email you a secure link to set a new one.
              </p>

              <form onSubmit={submit} className="mt-8 space-y-4">
                <div>
                  <label className="eyebrow block mb-2">Email address</label>
                  <div className={`flex items-center gap-2 px-4 py-3 rounded-2xl border-2 bg-brand-bg transition-colors ${emailError ? "border-red-300" : "border-brand focus-within:border-brand-primary"}`}>
                    <Mail size={16} className="text-brand-muted" strokeWidth={1.6} />
                    <input
                      type="email"
                      required
                      autoFocus
                      value={email}
                      onChange={(e) => { setEmail(e.target.value); if (emailError) setEmailError(""); }}
                      className="flex-1 bg-transparent outline-none text-brand-text placeholder:text-brand-muted/60"
                      placeholder="you@example.com"
                      data-testid="forgot-email-input"
                    />
                  </div>
                  {emailError && (
                    <p className="mt-2 text-xs text-red-600" data-testid="forgot-email-error">{emailError}</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary w-full justify-center group"
                  data-testid="forgot-submit-btn"
                >
                  {loading ? "Sending…" : (
                    <>
                      Send reset link
                      <ArrowRight size={16} className="ml-2 group-hover:translate-x-0.5 transition-transform" strokeWidth={1.8} />
                    </>
                  )}
                </button>
              </form>
            </>
          ) : (
            <div className="text-center py-2" data-testid="forgot-sent-confirmation">
              <div className="mx-auto w-14 h-14 rounded-full bg-brand-primary-light flex items-center justify-center mb-5">
                <MailCheck size={28} className="text-brand-primary" strokeWidth={1.6} />
              </div>
              <h1 className="font-serif-display text-3xl text-brand-text">Check your inbox</h1>
              <p className="text-brand-muted mt-3 leading-relaxed">
                If an account exists for <span className="text-brand-text font-medium">{email}</span>, we've sent a password
                reset link. It expires in <strong>24 hours</strong>.
              </p>
              <p className="text-xs text-brand-muted mt-5">
                Didn't see it? Check spam, or {" "}
                <button
                  type="button"
                  onClick={() => { setSent(false); }}
                  className="text-brand-primary hover:text-brand-wine underline"
                  data-testid="forgot-resend-link"
                >
                  try again with a different email
                </button>.
              </p>
            </div>
          )}

          <div className="mt-6 pt-5 border-t border-brand text-center">
            <Link
              to="/login"
              className="inline-flex items-center gap-1.5 text-sm text-brand-muted hover:text-brand-primary transition-colors"
              data-testid="forgot-back-to-login"
            >
              <ArrowLeft size={14} strokeWidth={1.8} />
              Back to sign in
            </Link>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
